import { html, css} from 'lit-element';
import { BaseElement } from './BaseElement';
import { API, EVENT_TYPES } from '../API';
import { PatientProgressDeliveryDays } from '../PatientProgressDeliveryDays';
import * as GUID from '../GUID';
import { DIALOG_TYPE_ERROR } from './ModalDialog';

const _api = Symbol();
const _statusHandler = Symbol();
const _deliveryDays = Symbol();

export class DeliveryOptions extends BaseElement {
	constructor() {
		super();

		this[_statusHandler] = () => {
			if(this.api.loggedIn && !this.deliveryDays && this.api.isPhysician)
				this.getDeliveryDays();
			else if(!this.api.loggedIn)
				this.kneeProtocols = null;
			this.requestUpdate();
		};
	}
	static get styles() {
		return [
			...BaseElement.styles,
			css`
				:host {
					display: block;
				}
				fieldset {
					// margin: 0.5em;
				}
				legend {
					padding: 0 0.5em;
					border-bottom: none;
					font-weight: bold;
				}
				.range {
					display: flex;
					flex-flow: row wrap;
				}
				.range > * {
					flex: 1 0;
					white-space: nowrap;
					border: var(--theme-border, black);
					border-radius: 0.25em;
					background-color: var(--theme-background-hover, white);
					padding: 0.25em;
					margin: 0.25em;
				}
			`
		];
	}
	get api() {
		return this[_api];
	}
	set api(v) {
		if(v !== null && !(v instanceof API))
			throw new TypeError('api must be an instance of API.');
		this[_api] = v;
	}
	get deliveryDays() {
		return this[_deliveryDays];
	}
	set deliveryDays(v) {
		if(v !== null && !(v instanceof PatientProgressDeliveryDays))
			throw new TypeError('deliveryDays must be an instance of PatientProgressDeliveryDays.');
		this[_deliveryDays] = v;

		this.requestUpdate();
	}
	async getDeliveryDays() {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			if(!this.api)
				throw new Error('no api set.');
			this.deliveryDays = await this.api.doctorPatientProgressDeliveryDays();
			this.emit('hide-busy', id);
		}
		catch(err) {
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Delivery Options',
				message: err.message,
				type: DIALOG_TYPE_ERROR
			});
		}
	}
	async setDeliveryDays() {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			if(!this.api)
				throw new Error('no api set.');
			await this.api.setDoctorPatientDeliveryDays(this.deliveryDays.value);
			this.emit('hide-busy', id);
		}
		catch(err) {
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Delivery Options',
				message: err.message,
				type: DIALOG_TYPE_ERROR
			});
		}
	}
	connectedCallback() {
		super.connectedCallback();

		this.emit('get-api', { callback: api => this.api = api });

		this.api.on(EVENT_TYPES.EVENT_TYPE_LOGIN_STATUS, this[_statusHandler]);

		if(this.api.loggedIn && this.api.isPhysician)
			this.getDeliveryDays();
	}
	disconnectedCallback() {
		super.disconnectedCallback();

		this.api.off(null, this[_statusHandler]);

		this.api = null;
	}
	async handleChange(e) {
		if(!this.deliveryDays)
			return;
		[...this.shadowRoot.querySelector('form').elements].forEach(v => {
			this.deliveryDays[v.getAttribute('id')] = v.checked;
		});

		this.setDeliveryDays();
	}
	render() {
		const deliveryDays = this.deliveryDays || PatientProgressDeliveryDays.fromString('0');
		return html`
			<fieldset>
				<legend>Progress Note Delivery Options</legend>
				<p class="info">
					<i class="icon fas fa-info-circle"></i>
					Please select when you would like progress notes of <strong>active</strong> patients emailed to you. Patients who are no longer using Kinex equipment may still be searched for within the Kinex Physician Portal.
				</p>
				<form @change="${e => this.handleChange(e)}" @submit="${e => e.preventDefault()}">
					<div><strong>By Day of Week</strong></div>
					<div class="range">
						<label>
							<span class="checkbox">
								<input id="sunday" type="checkbox" ?checked="${deliveryDays.sunday}"/>
								<label for="sunday"></label>
							</span>
							Sunday
						</label>
						<label>
							<span class="checkbox">
								<input id="monday" type="checkbox" ?checked="${deliveryDays.monday}"/>
								<label for="monday"></label>
							</span>
							Monday
						</label>
						<label>
							<span class="checkbox">
								<input id="tuesday" type="checkbox" ?checked="${deliveryDays.tuesday}"/>
								<label for="tuesday"></label>
							</span>
							Tuesday
						</label>
						<label>
							<span class="checkbox">
								<input id="wednesday" type="checkbox" ?checked="${deliveryDays.wednesday}"/>
								<label for="wednesday"></label>
							</span>
							Wednesday
						</label>
						<label>
							<span class="checkbox">
								<input id="thursday" type="checkbox" ?checked="${deliveryDays.thursday}"/>
								<label for="thursday"></label>
							</span>
							Thursday
						</label>
						<label>
							<span class="checkbox">
								<input id="friday" type="checkbox" ?checked="${deliveryDays.friday}"/>
								<label for="friday"></label>
							</span>
							Friday
						</label>
						<label>
							<span class="checkbox">
								<input id="saturday" type="checkbox" ?checked="${deliveryDays.saturday}"/>
								<label for="saturday"></label>
							</span>
							Saturday
						</label>
					</div>
					<hr />
					<div><strong>By Day of Patient Recovery</strong></div>
					<div class="range">
						<label>
							<span class="checkbox">
								<input id="day1" type="checkbox" ?checked="${deliveryDays.day1}"/>
								<label for="day1"></label>
							</span>
							Day 1
						</label>
						<label>
							<span class="checkbox">
								<input id="day2" type="checkbox" ?checked="${deliveryDays.day2}"/>
								<label for="day2"></label>
							</span>
							Day 2
						</label>
						<label>
							<span class="checkbox">
								<input id="day3" type="checkbox" ?checked="${deliveryDays.day3}"/>
								<label for="day3"></label>
							</span>
							Day 3
						</label>
						<label>
							<span class="checkbox">
								<input id="day4" type="checkbox" ?checked="${deliveryDays.day4}"/>
								<label for="day4"></label>
							</span>
							Day 4
						</label>
						<label>
							<span class="checkbox">
								<input id="day5" type="checkbox" ?checked="${deliveryDays.day5}"/>
								<label for="day5"></label>
							</span>
							Day 5
						</label>
						<label>
							<span class="checkbox">
								<input id="day6" type="checkbox" ?checked="${deliveryDays.day6}"/>
								<label for="day6"></label>
							</span>
							Day 6
						</label>
						<label>
							<span class="checkbox">
								<input id="day7" type="checkbox" ?checked="${deliveryDays.day7}"/>
								<label for="day7"></label>
							</span>
							Day 7
						</label>
					</div>
					<div class="range">
						<label>
							<span class="checkbox">
								<input id="day8" type="checkbox" ?checked="${deliveryDays.day8}"/>
								<label for="day8"></label>
							</span>
							Day 8
						</label>
						<label>
							<span class="checkbox">
								<input id="day9" type="checkbox" ?checked="${deliveryDays.day9}"/>
								<label for="day9"></label>
							</span>
							Day 9
						</label>
						<label>
							<span class="checkbox">
								<input id="day10" type="checkbox" ?checked="${deliveryDays.day10}"/>
								<label for="day10"></label>
							</span>
							Day 10
						</label>
						<label>
							<span class="checkbox">
								<input id="day11" type="checkbox" ?checked="${deliveryDays.day11}"/>
								<label for="day11"></label>
							</span>
							Day 11
						</label>
						<label>
							<span class="checkbox">
								<input id="day12" type="checkbox" ?checked="${deliveryDays.day12}"/>
								<label for="day12"></label>
							</span>
							Day 12
						</label>
						<label>
							<span class="checkbox">
								<input id="day13" type="checkbox" ?checked="${deliveryDays.day13}"/>
								<label for="day13"></label>
							</span>
							Day 13
						</label>
						<label>
							<span class="checkbox">
								<input id="day14" type="checkbox" ?checked="${deliveryDays.day14}"/>
								<label for="day14"></label>
							</span>
							Day 14
						</label>
					</div>
					<div class="range">
						<label>
							<span class="checkbox">
								<input id="day15" type="checkbox" ?checked="${deliveryDays.day15}"/>
								<label for="day15"></label>
							</span>
							Day 15
						</label>
						<label>
							<span class="checkbox">
								<input id="day16" type="checkbox" ?checked="${deliveryDays.day16}"/>
								<label for="day16"></label>
							</span>
							Day 16
						</label>
						<label>
							<span class="checkbox">
								<input id="day17" type="checkbox" ?checked="${deliveryDays.day17}"/>
								<label for="day17"></label>
							</span>
							Day 17
						</label>
						<label>
							<span class="checkbox">
								<input id="day18" type="checkbox" ?checked="${deliveryDays.day18}"/>
								<label for="day18"></label>
							</span>
							Day 18
						</label>
						<label>
							<span class="checkbox">
								<input id="day19" type="checkbox" ?checked="${deliveryDays.day19}"/>
								<label for="day19"></label>
							</span>
							Day 19
						</label>
						<label>
							<span class="checkbox">
								<input id="day20" type="checkbox" ?checked="${deliveryDays.day20}"/>
								<label for="day20"></label>
							</span>
							Day 20
						</label>
						<label>
							<span class="checkbox">
								<input id="day21" type="checkbox" ?checked="${deliveryDays.day21}"/>
								<label for="day21"></label>
							</span>
							Day 21
						</label>
					</div>
					<div class="range">
						<label>
							<span class="checkbox">
								<input id="day22" type="checkbox" ?checked="${deliveryDays.day22}"/>
								<label for="day22"></label>
							</span>
							Day 22
						</label>
						<label>
							<span class="checkbox">
								<input id="day23" type="checkbox" ?checked="${deliveryDays.day23}"/>
								<label for="day23"></label>
							</span>
							Day 23
						</label>
						<label>
							<span class="checkbox">
								<input id="day24" type="checkbox" ?checked="${deliveryDays.day24}"/>
								<label for="day24"></label>
							</span>
							Day 24
						</label>
						<label>
							<span class="checkbox">
								<input id="day25" type="checkbox" ?checked="${deliveryDays.day25}"/>
								<label for="day25"></label>
							</span>
							Day 25
						</label>
						<label>
							<span class="checkbox">
								<input id="day26" type="checkbox" ?checked="${deliveryDays.day26}"/>
								<label for="day26"></label>
							</span>
							Day 26
						</label>
						<label>
							<span class="checkbox">
								<input id="day27" type="checkbox" ?checked="${deliveryDays.day27}"/>
								<label for="day27"></label>
							</span>
							Day 27
						</label>
						<label>
							<span class="checkbox">
								<input id="day28" type="checkbox" ?checked="${deliveryDays.day28}"/>
								<label for="day28"></label>
							</span>
							Day 28
						</label>
					</div>
					<div class="range">
						<label>
							<span class="checkbox">
								<input id="day29" type="checkbox" ?checked="${deliveryDays.day29}"/>
								<label for="day29"></label>
							</span>
							Day 29
						</label>
						<label>
							<span class="checkbox">
								<input id="day30" type="checkbox" ?checked="${deliveryDays.day30}"/>
								<label for="day30"></label>
							</span>
							Day 30
						</label>
						<label>
							<span class="checkbox">
								<input id="day31" type="checkbox" ?checked="${deliveryDays.day31}"/>
								<label for="day31"></label>
							</span>
							Day 31
						</label>
						<label>
							<span class="checkbox">
								<input id="day32" type="checkbox" ?checked="${deliveryDays.day32}"/>
								<label for="day32"></label>
							</span>
							Day 32
						</label>
						<label>
							<span class="checkbox">
								<input id="day33" type="checkbox" ?checked="${deliveryDays.day33}"/>
								<label for="day33"></label>
							</span>
							Day 33
						</label>
						<label>
							<span class="checkbox">
								<input id="day34" type="checkbox" ?checked="${deliveryDays.day34}"/>
								<label for="day34"></label>
							</span>
							Day 34
						</label>
						<label>
							<span class="checkbox">
								<input id="day35" type="checkbox" ?checked="${deliveryDays.day35}"/>
								<label for="day35"></label>
							</span>
							Day 35
						</label>
					</div>
					<div class="range">
						<label>
							<span class="checkbox">
								<input id="day36" type="checkbox" ?checked="${deliveryDays.day36}"/>
								<label for="day36"></label>
							</span>
							Day 36
						</label>
						<label>
							<span class="checkbox">
								<input id="day37" type="checkbox" ?checked="${deliveryDays.day37}"/>
								<label for="day37"></label>
							</span>
							Day 37
						</label>
						<label>
							<span class="checkbox">
								<input id="day38" type="checkbox" ?checked="${deliveryDays.day38}"/>
								<label for="day38"></label>
							</span>
							Day 38
						</label>
						<label>
							<span class="checkbox">
								<input id="day39" type="checkbox" ?checked="${deliveryDays.day39}"/>
								<label for="day39"></label>
							</span>
							Day 39
						</label>
						<label>
							<span class="checkbox">
								<input id="day40" type="checkbox" ?checked="${deliveryDays.day40}"/>
								<label for="day40"></label>
							</span>
							Day 40
						</label>
						<label>
							<span class="checkbox">
								<input id="day41" type="checkbox" ?checked="${deliveryDays.day41}"/>
								<label for="day41"></label>
							</span>
							Day 41
						</label>
						<label>
							<span class="checkbox">
								<input id="day42" type="checkbox" ?checked="${deliveryDays.day42}"/>
								<label for="day42"></label>
							</span>
							Day 42
						</label>
					</div>
					<div class="range">
						<label>
							<span class="checkbox">
								<input id="day43" type="checkbox" ?checked="${deliveryDays.day43}"/>
								<label for="day43"></label>
							</span>
							Day 43
						</label>
						<label>
							<span class="checkbox">
								<input id="day44" type="checkbox" ?checked="${deliveryDays.day44}"/>
								<label for="day44"></label>
							</span>
							Day 44
						</label>
						<label>
							<span class="checkbox">
								<input id="day45" type="checkbox" ?checked="${deliveryDays.day45}"/>
								<label for="day45"></label>
							</span>
							Day 45
						</label>
						<label>
							<span class="checkbox">
								<input id="day46" type="checkbox" ?checked="${deliveryDays.day46}"/>
								<label for="day46"></label>
							</span>
							Day 46
						</label>
						<label>
							<span class="checkbox">
								<input id="day47" type="checkbox" ?checked="${deliveryDays.day47}"/>
								<label for="day47"></label>
							</span>
							Day 47
						</label>
						<label>
							<span class="checkbox">
								<input id="day48" type="checkbox" ?checked="${deliveryDays.day48}"/>
								<label for="day48"></label>
							</span>
							Day 48
						</label>
						<label>
							<span class="checkbox">
								<input id="day49" type="checkbox" ?checked="${deliveryDays.day49}"/>
								<label for="day49"></label>
							</span>
							Day 49
						</label>
					</div>
				</form>
			</fieldset>
		`;
	}
}

customElements.define('pp-delivery-options', DeliveryOptions);